import { useEffect, useRef, useState } from 'react';
import { getFreshWsUrl, invalidateWsToken } from '@/lib/ws-url';
import { isAuthGenuinelyLost } from '@/lib/auth-refusal';
import { backoffDelayMs } from '@/lib/backoff';
import {
  buildPing,
  deriveQuality,
  parsePong,
  rttMs,
  stddev,
  type ConnQuality,
  type PongFrame,
} from './bff';

// =====================================================================
// useBffPing — opens a WebSocket to the BFF (`${base}/ws`) and drives a
// client-side ping/pong: a `ping` on connect and every `intervalMs`. Beyond a
// simple liveness dot it derives a small LINK-HEALTH picture for the Twilio
// control box's "connection quality" readout:
//   - rtt           last round-trip (ms)
//   - jitterMs      stddev of the last ~20 RTTs (how bouncy the link is)
//   - missCount     pings whose pong never came back within a timeout
//   - reconnectCount socket reopens since mount
//   - lastPongAgeMs ms since the last pong (a "are packets still flowing?" age)
//   - online        navigator.onLine (+ online/offline events)
//   - quality       coarse bucket (excellent/good/degraded/offline)
// WebSockets give us no per-packet ACKs, so "misses" + pong-age stand in as the
// packet-reliability signal. Reconnects on close.
// =====================================================================

export type WsStatus = 'connecting' | 'open' | 'closed';

export interface PongReadout {
  frame: PongFrame;
  rtt: number | null;
}

export interface BffHealth {
  status: WsStatus;
  lastPong: PongReadout | null;
  pongCount: number;
  rtt: number | null;
  jitterMs: number;
  missCount: number;
  reconnectCount: number;
  lastPongAgeMs: number | null;
  online: boolean;
  quality: ConnQuality;
}

const RTT_WINDOW = 20; // RTTs kept for the jitter (stddev) calc

// Retry ceiling. This socket reconnects on every close and was HALF the observed
// flood when the cockpit JWT stopped verifying. The trade: under a sustained outage
// `reconnectCount` now under-reports how hard the link is flapping, though `quality`
// still reads offline.
const RECONNECT_MAX_MS = 30_000;

// `enabled: false` opens NO socket and reports `closed` — the single-websocket
// rollout (plans/single-websocket/plan.md): when the BFF's hello says `singleWs`,
// the cockpit reads link health off its own socket and this one stays shut. The
// flag can flip at runtime (a reconnect re-reads it), so the socket effect keys
// on it: disabling tears the socket down, re-enabling reopens it.
export function useBffPing(
  impersonateSdr: string | null,
  opts: { intervalMs?: number; enabled?: boolean } = {},
): BffHealth {
  const { intervalMs = 2000, enabled = true } = opts;
  const [status, setStatus] = useState<WsStatus>('connecting');
  const [lastPong, setLastPong] = useState<PongReadout | null>(null);
  const [pongCount, setPongCount] = useState(0);
  const [rtt, setRtt] = useState<number | null>(null);
  const [jitterMs, setJitterMs] = useState(0);
  const [missCount, setMissCount] = useState(0);
  const [reconnectCount, setReconnectCount] = useState(0);
  const [lastPongAgeMs, setLastPongAgeMs] = useState<number | null>(null);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  // Monotonic ping counter; outstanding pings (n -> sentAt) for miss detection;
  // recent RTTs for jitter; epoch of the last pong for the age/stale signal.
  const nRef = useRef(0);
  const pending = useRef<Map<number, number>>(new Map());
  const rttWindow = useRef<number[]>([]);
  const lastPongAt = useRef<number | null>(null);

  // A ping unanswered for this long is counted as a dropped packet; pongs quiet
  // for this long mark the link "stale" (→ degraded quality, recovers on resume).
  const missTimeoutMs = intervalMs * 3;
  const staleMs = intervalMs * 2.5;

  // Browser online/offline — a fast path to the `offline` quality bucket.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // 1Hz tick: age the last pong, and sweep timed-out pings into missCount. Runs
  // independent of the socket so the age keeps climbing while disconnected.
  useEffect(() => {
    const tick = setInterval(() => {
      const now = Date.now();
      let swept = 0;
      for (const [n, sentAt] of pending.current) {
        if (now - sentAt > missTimeoutMs) {
          pending.current.delete(n);
          swept += 1;
        }
      }
      if (swept) setMissCount((c) => c + swept);
      setLastPongAgeMs(lastPongAt.current === null ? null : now - lastPongAt.current);
    }, 1000);
    return () => clearInterval(tick);
  }, [missTimeoutMs]);

  useEffect(() => {
    // Disabled (single-websocket mode): no socket, and an honest `closed` so any
    // leftover reader shows a dead probe rather than a stale-but-green one.
    if (!enabled) {
      setStatus('closed');
      return;
    }
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let closed = false; // set on unmount so a late onclose doesn't reconnect
    let everOpened = false; // first connect isn't a "reconnect"
    let retryAttempt = 0; // consecutive failures, for the capped backoff
    let authRefusals = 0; // consecutive 1008s; see AUTH_REFUSALS_BEFORE_REAUTH
    let firstRefusalAt: number | null = null;

    const ping = (): void => {
      if (ws?.readyState !== WebSocket.OPEN) return;
      nRef.current += 1;
      const n = nRef.current;
      pending.current.set(n, Date.now());
      ws.send(JSON.stringify(buildPing(n)));
    };

    const connect = async (): Promise<void> => {
      setStatus('connecting');
      // Fresh token per attempt; the await means the effect may be gone by the time
      // we resume, so re-check before opening a socket nothing would clean up.
      const url = await getFreshWsUrl(impersonateSdr, 'ping');
      if (closed) return;
      // Declare what this socket is FOR: a link-health probe, not a cockpit.
      // Harmless while the BFF ignores it; a BFF that honours the role stops
      // giving this socket a phantom cockpit session.
      ws = new WebSocket(`${url}${url.includes('?') ? '&' : '?'}role=ping`);

      ws.onopen = () => {
        setStatus('open');
        if (everOpened) setReconnectCount((c) => c + 1);
        everOpened = true;
        ping(); // probe immediately on connect
        timer = setInterval(ping, intervalMs);
      };

      ws.onmessage = (evt: MessageEvent) => {
        const pong = parsePong(evt.data);
        if (!pong) return; // ignore hello / dialer_outcome / unknown frames
        const r = rttMs(pong);
        if (typeof pong.n === 'number') pending.current.delete(pong.n);
        if (r !== null) {
          const w = rttWindow.current;
          w.push(r);
          if (w.length > RTT_WINDOW) w.shift();
          setJitterMs(stddev(w));
          setRtt(r);
        }
        // A pong proves the socket survived the BFF's post-upgrade auth check, so
        // this — not `onopen` — is what clears the backoff (a rejected socket still
        // fires onopen before its 1008 close).
        retryAttempt = 0;
        authRefusals = 0;
        firstRefusalAt = null;
        lastPongAt.current = Date.now();
        setLastPongAgeMs(0);
        setLastPong({ frame: pong, rtt: r });
        setPongCount((c) => c + 1);
      };

      ws.onclose = (evt: CloseEvent) => {
        if (timer) clearInterval(timer);
        if (closed) return;
        setStatus('closed');
        if (evt.code === 1008) {
          invalidateWsToken();
          authRefusals += 1;
          firstRefusalAt ??= Date.now();
        }
        // Back off to the ceiling, never stop: this socket can't see whether a call is
        // live, so useCockpit owns the re-auth decision — and it declines to redirect
        // mid-call, so quitting here leaves the readout dead for the rest of the shift.
        const authLost = isAuthGenuinelyLost(authRefusals, firstRefusalAt, Date.now());
        retryAttempt += 1;
        const delay = authLost
          ? Math.random() * RECONNECT_MAX_MS
          : backoffDelayMs(retryAttempt, intervalMs, RECONNECT_MAX_MS);
        retry = setTimeout(() => void connect(), delay);
      };

      ws.onerror = () => ws?.close();
    };

    void connect();

    return () => {
      closed = true;
      if (timer) clearInterval(timer);
      if (retry) clearTimeout(retry);
      ws?.close();
    };
  }, [impersonateSdr, intervalMs, enabled]);

  const open = status === 'open';
  const stale = lastPongAgeMs !== null && lastPongAgeMs > staleMs;
  const quality = deriveQuality({ open, online, rttMs: rtt, jitterMs, stale });

  return {
    status,
    lastPong,
    pongCount,
    rtt,
    jitterMs,
    missCount,
    reconnectCount,
    lastPongAgeMs,
    online,
    quality,
  };
}
